import {useAppDispatch, useAppSelector, ConnectionUser} from '@lib/store';
import {actions} from '@lib/store/src/data/timeslots/actions';
import {LocalDateString, WorkspaceReservation, Workspace} from '@lib/store/src/data/timeslots/apiTypes';
import {localDateStringToStartOfDay} from '@lib/store/src/data/timeslots/dateTimeHelpers';
import {groupBy} from '@lib/utils';
import {useEffect} from 'react';

// TODO: extract to libs
// Note: this kind of should replace this selector:
// getAllBuildingLocations
export const useConnectionsSchedule = (date: LocalDateString) => {
  const dispatch = useAppDispatch();

  const workdayEntry = useAppSelector((state) => {
    return state.data.timeslots.connectionWorkdaysByDate[date];
  });

  const workspaceReservationEntry = useAppSelector((state) => {
    return state.data.timeslots.connectionWorkspaceReservationsByDate[date];
  });

  useEffect(
    function ensureReservationsLoaded() {
      if (!workspaceReservationEntry) {
        dispatch(actions.fetchWorkspaceReservationsForConnections({date}));
      }
    },
    [date, workspaceReservationEntry],
  );

  useEffect(
    function ensureWorkdaysLoaded() {
      if (!workdayEntry) {
        dispatch(
          actions.fetchWorkdaysForConnections({
            from: localDateStringToStartOfDay(date),
            to: localDateStringToStartOfDay(date),
          }),
        );
      }
    },
    [date, workdayEntry],
  );

  const error = workdayEntry?.status === 'rejected' || workspaceReservationEntry?.status === 'rejected';
  const pending =
    !workdayEntry ||
    !workspaceReservationEntry ||
    workdayEntry.status === 'pending' ||
    workspaceReservationEntry.status === 'pending';

  // Group connections by their location
  return {
    status: pending ? 'pending' : error ? 'error' : 'fulfilled',
    workdays: workdayEntry?.data ?? [],
    workspaceResevations: workspaceReservationEntry?.data ?? [],
  } as const;
};

type ReferenceLocation = {buildingId: string; floorId?: string; areaId?: string};

export const constructConnectionsOverview = (
  connectedUsers: Array<ConnectionUser>,
  connectionWorkspaceReservations: Array<WorkspaceReservation>,
  referenceLocation?: ReferenceLocation,
  groupLevel: 'building' | 'floor' | 'area' = referenceLocation?.areaId ? 'area' : 'building',
): Array<{
  locationName: string;
  isUserWorkspace: boolean;
  occupants: Array<{
    user: ConnectionUser;
    reservations: Array<WorkspaceReservation>;
  }>;
}> => {
  const relevantReservations = referenceLocation
    ? connectionWorkspaceReservations.filter((r) => r.workspace.buildingId === referenceLocation.buildingId)
    : connectionWorkspaceReservations;

  // We create groups per area or floor or building
  const getReservationGroupId = (loc: ReferenceLocation) => {
    if (groupLevel === 'area') {
      return loc.areaId ?? loc.floorId ?? loc.buildingId;
    }

    if (groupLevel === 'floor') {
      return loc.floorId ?? loc.buildingId;
    }

    return loc.buildingId;
  };

  const userWorkspaceId = referenceLocation ? getReservationGroupId(referenceLocation) : Symbol();

  // Index bookings by location
  const reservationsByWorkspace = groupBy(relevantReservations, (r) => getReservationGroupId(r.workspace));

  // Index connections by user id
  const connectionsByUserId = new Map(connectedUsers.map((c) => [c.id, c]));

  return Object.entries(reservationsByWorkspace)
    .map(([workspaceId, reservations]) => {
      const {workspace} = reservations[0];

      const byUserId = groupBy(reservations, (r) => r.ownerId);

      const floorName = 'floorName' in workspace ? workspace.floorName : undefined;
      const areaName = 'areaName' in workspace ? workspace.areaName : undefined;

      return {
        isUserWorkspace: workspaceId === userWorkspaceId,

        locationName:
          groupLevel === 'area'
            ? floorName && areaName
              ? `${floorName}, ${areaName}`
              : floorName || ''
            : groupLevel === 'floor'
            ? floorName || ''
            : workspace.buildingName,

        occupants: Object.entries(byUserId)
          .flatMap(([userId, reservations]) => {
            const user = connectionsByUserId.get(userId);
            if (!user) {
              console.warn('Warning: received reservation for which user was not found in connections');
              return [];
            }

            return {
              user,
              reservations,
            };
          })
          .sort((a, b) => a.user.name.localeCompare(b.user.name)),
      };
    })
    .sort((a, b) => {
      if (a.isUserWorkspace) return -1;
      if (b.isUserWorkspace) return 1;

      return a.locationName.localeCompare(b.locationName);
    });
};
